export type DeviceUxMode =
  | 'iphone-touch'
  | 'ipad-touch'
  | 'ipad-pointer'
  | 'desktop-pointer'
  | 'watch-companion';

export type DeviceOrientation = 'portrait' | 'landscape';

export interface DeviceUxInput {
  width: number;
  height: number;
  hasTouch: boolean;
  primaryPointerFine: boolean;
  anyPointerFine: boolean;
  hover: boolean;
  anyHover: boolean;
  reducedMotion: boolean;
}

export interface DeviceUxProfile extends DeviceUxInput {
  mode: DeviceUxMode;
  orientation: DeviceOrientation;
  supportsPointerPrecision: boolean;
  isCompactTouch: boolean;
}

const IPHONE_MAX_SHORT_SIDE = 480;
const IPHONE_MAX_LONG_SIDE = 960;
const WATCH_MAX_SIDE = 260;

export function classifyDeviceUx(input: DeviceUxInput): DeviceUxProfile {
  const shortSide = Math.min(input.width, input.height);
  const longSide = Math.max(input.width, input.height);
  const orientation: DeviceOrientation = input.width >= input.height ? 'landscape' : 'portrait';
  const supportsPointerPrecision = input.anyPointerFine && input.anyHover;

  let mode: DeviceUxMode;
  if (input.hasTouch && longSide <= WATCH_MAX_SIDE) {
    mode = 'watch-companion';
  } else if (
    input.hasTouch &&
    shortSide <= IPHONE_MAX_SHORT_SIDE &&
    longSide <= IPHONE_MAX_LONG_SIDE
  ) {
    mode = 'iphone-touch';
  } else if (input.hasTouch) {
    mode = supportsPointerPrecision ? 'ipad-pointer' : 'ipad-touch';
  } else {
    mode = 'desktop-pointer';
  }

  return {
    ...input,
    mode,
    orientation,
    supportsPointerPrecision,
    isCompactTouch: mode === 'iphone-touch' || mode === 'watch-companion',
  };
}

export function getDeviceUxSnapshot(): DeviceUxInput {
  if (typeof window === 'undefined') {
    return {
      width: 1280,
      height: 800,
      hasTouch: false,
      primaryPointerFine: true,
      anyPointerFine: true,
      hover: true,
      anyHover: true,
      reducedMotion: false,
    };
  }

  const match = (query: string) => window.matchMedia?.(query).matches ?? false;
  const navigatorWithTouch = navigator as Navigator & { maxTouchPoints?: number };
  const hasTouch = (navigatorWithTouch.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
  const screenWidth = window.screen?.width || window.innerWidth;
  const screenHeight = window.screen?.height || window.innerHeight;

  return {
    width: hasTouch ? Math.min(window.innerWidth, screenWidth) : window.innerWidth,
    height: hasTouch ? Math.min(window.innerHeight, screenHeight) : window.innerHeight,
    hasTouch,
    primaryPointerFine: match('(pointer: fine)'),
    anyPointerFine: match('(any-pointer: fine)'),
    hover: match('(hover: hover)'),
    anyHover: match('(any-hover: hover)'),
    reducedMotion: match('(prefers-reduced-motion: reduce)'),
  };
}
